import React, { ChangeEventHandler, forwardRef, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { ReservationFlowProductCustomerFieldItem } from 'src/interfaces/reservation_flow';
import { useDataAttributeProps } from 'src/hooks/useDataAttributeProps';
import { RSCheckBoxWithLabel } from 'src/reservation-page/RSCheckBoxWithLabel';

export interface CustomerInfoInputCheckBoxProps {
  [key: `data-${string}`]: string | undefined;
  value?: string[];
  onChange: (value: string[]) => void;
  onBlur: () => void;
  item: ReservationFlowProductCustomerFieldItem;
}

export const CustomerInfoInputCheckBox = forwardRef<
  HTMLDivElement,
  CustomerInfoInputCheckBoxProps
>((props, ref) => {
  const { value, onChange, onBlur, item } = props;
  const dataAttributes = useDataAttributeProps(props);

  const intl = useIntl();

  const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const changedValue = event.target.value;

      if (!value) {
        onChange([changedValue]);
      } else if (value.includes(changedValue)) {
        const newValue = value.filter((v) => v !== changedValue);

        onChange(newValue);
      } else {
        onChange([...value, changedValue]);
      }

      onBlur();
    },
    [value, onChange, onBlur],
  );

  return (
    <div ref={ref}>
      <div className="mb-1.5">
        <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
          {item.description.length > 0 ? item.description : item.label}
          {item.is_required && (
            <span className="text-red-700">
              {intl.formatMessage({
                defaultMessage: '（必須）',
                id: 'I4vw7z',
                description:
                  'src/organisms/product-purchase/CustomerInfoInputCheckBox/index.tsx',
              })}
            </span>
          )}
        </div>
      </div>
      <div>
        {item.options &&
          item.options.map((option) => {
            return (
              <div
                key={`questionnaire_${item.id}_${option}`}
                className="flex h-44 items-center"
              >
                <RSCheckBoxWithLabel
                  {...dataAttributes}
                  labelText={option}
                  value={option}
                  name={`questionnaire_${item.id}`}
                  checked={
                    typeof value !== 'undefined'
                      ? value.includes(option)
                      : false
                  }
                  onChange={onInputChange}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
});

CustomerInfoInputCheckBox.displayName = 'CustomerInfoInputCheckBox';
